import * as z from "zod/v4";

const timePeriodSchema = z.enum([
  "day",
  "week",
  "month",
  "last-month",
  "quarter",
  "all",
  "custom",
]);

const customRangeSchema = z.object({
  start: z.string().min(1),
  end: z.string().min(1),
});

export const onboardingV2StatusSchema = z.enum([
  "partial-completion",
  "completed",
  "help-requested",
]);

export type OnboardingV2Status = z.infer<typeof onboardingV2StatusSchema>;

export const onboardingV2RowSchema = z.object({
  id: z.string(),
  teamId: z.string(),
  productId: z.string(),
  version: z.number(),
  status: onboardingV2StatusSchema,
  currentStepId: z.string(),
  dealSize: z.number().nullable(),
  startedAt: z.string(),
  updatedAt: z.string(),
  completedAt: z.string().nullable(),
  startedByUserId: z.string().nullable(),
  lastUpdatedByUserId: z.string().nullable(),
  stepsCompleted: z.number(),
  totalSteps: z.number(),
  totalTimeMs: z.number(),
  teamName: z.string(),
  teamDomain: z.string().nullable(),
  hubspotDealId: z.string().nullable(),
});

export type OnboardingV2Row = z.infer<typeof onboardingV2RowSchema>;

export const getOnboardingsV2 = {
  name: "getOnboardingsV2" as const,
  input: z
    .object({
      limit: z.number().min(1).max(100).default(20),
      offset: z.number().min(0).default(0),
      productId: z.string().optional(),
      statuses: z.array(onboardingV2StatusSchema).optional(),
      searchQuery: z.string().optional(),
      timePeriod: timePeriodSchema,
      customRange: customRangeSchema.optional(),
      sortBy: z.enum(["updated_at", "started_at", "deal_size"]).optional(),
      sortOrder: z.enum(["asc", "desc"]).optional(),
    })
    .superRefine((value, ctx) => {
      if (value.timePeriod === "custom" && !value.customRange) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "customRange is required when timePeriod is custom",
          path: ["customRange"],
        });
      }
    }),
  output: z.object({ rows: z.array(onboardingV2RowSchema), total: z.number() }),
};

export type GetOnboardingsV2Input = z.infer<typeof getOnboardingsV2.input>;
export type GetOnboardingsV2Output = z.infer<typeof getOnboardingsV2.output>;
